import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Badge, Button, Group, Paper, SegmentedControl, Stack, Switch, Text } from "@mantine/core"
import { IconGripVertical } from "@tabler/icons-react"
import type { ISummaryBlockItem, IViewProps, TBlockColumn } from "../interfaces"
import classes from "./styles.module.css"

interface IRowProps {
  item: ISummaryBlockItem
  onToggle: (type: string, visible: boolean) => void
  onColumn: (type: string, column: TBlockColumn) => void
}

function SortableRow({ item, onToggle, onColumn }: IRowProps): React.JSX.Element {
  const { i18n } = useLingui()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.type })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  return (
    <Paper ref={setNodeRef} style={style} withBorder p="xs" className={classes.row} data-hidden={!item.visible}>
      <Group gap="sm" wrap="nowrap">
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={i18n._(msg`Drag to reorder`)}
          className={classes.handle}
          {...attributes}
          {...listeners}
        >
          <IconGripVertical size={18} />
        </ActionIcon>
        <Switch
          checked={item.visible}
          onChange={(e) => onToggle(item.type, e.currentTarget.checked)}
          aria-label={i18n._(msg`Show this block`)}
        />
        <Text size="sm" fw={500} c={item.visible ? undefined : "dimmed"} style={{ flex: 1 }}>
          {item.displayName}
        </Text>
        <SegmentedControl
          size="xs"
          value={item.column}
          onChange={(v) => {
            if (v === "main" || v === "side") onColumn(item.type, v)
          }}
          data={[
            { label: i18n._(msg`Main`), value: "main" },
            { label: i18n._(msg`Side`), value: "side" },
          ]}
        />
      </Group>
    </Paper>
  )
}

/**
 * Dumb curate view for the modular meeting-summary layout: a single sortable
 * list of blocks with a show/hide toggle and a Main/Side column control. The
 * smart wrapper owns the GET/PUT; this view is controlled (items in, events
 * out). Drag reorders within the list; on save the wrapper derives per-column
 * order from list position.
 */
export function SummaryLayoutPanelView({
  items,
  requiredNames,
  saving,
  dirty,
  onItemsChange,
  onSave,
  onReset,
}: IViewProps): React.JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex((i) => i.type === active.id)
    const newIndex = items.findIndex((i) => i.type === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onItemsChange(arrayMove(items, oldIndex, newIndex))
  }

  const patch = (type: string, change: Partial<ISummaryBlockItem>): void =>
    onItemsChange(items.map((i) => (i.type === type ? { ...i, ...change } : i)))

  return (
    <Stack gap="md">
      <div>
        <Text fw={600}>
          <Trans>Meeting summary layout</Trans>
        </Text>
        <Text size="sm" c="dimmed">
          <Trans>
            Choose which blocks appear on the meeting summary, in what order, and which column. Main is the primary
            column; Side is the rail. This applies to everyone in your workspace; on mobile the blocks stack in order.
          </Trans>
        </Text>
      </div>

      {requiredNames.length > 0 && (
        <Group gap={6}>
          <Text size="xs" c="dimmed">
            <Trans>Always shown:</Trans>
          </Text>
          {requiredNames.map((name) => (
            <Badge key={name} variant="light" color="gray" size="sm">
              {name}
            </Badge>
          ))}
        </Group>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.type)} strategy={verticalListSortingStrategy}>
          <Stack gap="xs">
            {items.map((item) => (
              <SortableRow
                key={item.type}
                item={item}
                onToggle={(type, visible) => patch(type, { visible })}
                onColumn={(type, column) => patch(type, { column })}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      <Group>
        <Button onClick={onSave} loading={saving} disabled={!dirty}>
          <Trans>Save layout</Trans>
        </Button>
        <Button variant="default" onClick={onReset} disabled={!dirty || saving}>
          <Trans>Reset</Trans>
        </Button>
      </Group>
    </Stack>
  )
}
